import { lazy } from 'react'
import { Navigate } from 'react-router-dom'

import {
  PoTemplateDetailDocument,
  ProcurementSettingsFeatureCheckDocument,
  RequestTypeDetailDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { NavigationRoutesFn } from 'modules/core/lib/react-router'

const PurchaseRequestsSettingsPage = lazy(() => import('modules/procurement/pages/SettingsPage'))

const RequestTypeDetailPage = lazy(() => import('modules/procurement/pages/RequestTypeDetailPage'))

const PoTemplateDetailPage = lazy(() => import('modules/procurement/pages/PoTemplateDetailPage'))

const settingsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.settings.procurement.path,
      handle: {
        meta: () => ({
          title: i18n.t('company_settings.finance.procurement.title'),
          illustration: 'receipt',
        }),
      },
      children: [
        {
          index: true,
          element: <Navigate to={navigation.settings.procurement.requests.path} replace />,
        },
        {
          path: navigation.settings.procurement.requests.path,
          element: <PurchaseRequestsSettingsPage />,
          handle: {
            meta: ({ watchQuery, session }) => {
              return watchQuery({
                query: ProcurementSettingsFeatureCheckDocument,
                variables: { companyId: session.company.id },
              }).map(({ data }) => {
                const isProcurementV2 = !!data?.features?.featuresConnection?.nodes?.some(
                  (f) => f.name === 'procurement_v2' && f.enabled
                )

                return {
                  title: isProcurementV2
                    ? i18n.t('procurement.purchase_requests.settings.sidebar.request_types')
                    : i18n.t('procurement.purchase_requests.settings.sidebar.requests'),
                }
              })
            },
          },
        },
        {
          path: navigation.settings.procurement.taskForms.path,
          element: <PurchaseRequestsSettingsPage />,
          handle: {
            meta: () => ({
              title: i18n.t('procurement.purchase_requests.settings.sidebar.forms'),
            }),
          },
        },
        {
          path: navigation.settings.procurement.poTemplates.path,
          element: <PurchaseRequestsSettingsPage />,
          handle: {
            meta: () => ({
              title: i18n.t('procurement.purchase_requests.settings.sidebar.po_templates'),
            }),
          },
        },
        {
          path: navigation.settings.procurement.poTemplate.path,
          handle: {
            meta: () => ({
              title: i18n.t('procurement.purchase_requests.settings.sidebar.po_templates'),
              backLink: { href: navigation.settings.procurement.poTemplates.fullPath() },
            }),
          },
          children: [
            {
              index: true,
              element: (
                <Navigate to={navigation.settings.procurement.poTemplates.fullPath()} replace />
              ),
            },
            {
              path: navigation.settings.procurement.poTemplate.id.path,
              element: <PoTemplateDetailPage />,
              handle: {
                resource: true,
                meta: ({ watchQuery, params }) => {
                  return watchQuery({
                    query: PoTemplateDetailDocument,
                    variables: {
                      poTemplateId: String(params.id),
                    },
                  }).map(({ data }) => {
                    const poTemplate = data?.procurement?.poTemplatesConnection?.edges?.[0]?.node

                    return {
                      title:
                        poTemplate?.name ||
                        i18n.t(
                          'procurement.purchase_requests.settings.po_templates.detail.breadcrumb'
                        ),
                      illustration: 'receipt',
                    }
                  })
                },
              },
            },
          ],
        },
        {
          path: navigation.settings.procurement.requestType.path,
          handle: {
            meta: ({ watchQuery, session }) => {
              return watchQuery({
                query: ProcurementSettingsFeatureCheckDocument,
                variables: { companyId: session.company.id },
              }).map(({ data }) => {
                const isProcurementV2 = !!data?.features?.featuresConnection?.nodes?.some(
                  (f) => f.name === 'procurement_v2' && f.enabled
                )

                return {
                  title: isProcurementV2
                    ? i18n.t('procurement.purchase_requests.settings.sidebar.request_types')
                    : i18n.t('procurement.purchase_requests.settings.sidebar.requests'),
                  backLink: { href: navigation.settings.procurement.requests.fullPath() },
                }
              })
            },
          },
          children: [
            {
              index: true,
              element: (
                <Navigate to={navigation.settings.procurement.requests.fullPath()} replace />
              ),
            },
            {
              path: navigation.settings.procurement.requestType.id.path,
              element: <RequestTypeDetailPage />,
              handle: {
                resource: true,
                meta: ({ watchQuery, params, session }) => {
                  return watchQuery({
                    query: RequestTypeDetailDocument,
                    variables: {
                      requestTypeId: String(params.id),
                      companyId: session.company.id,
                    },
                  }).map(({ data }) => {
                    const requestType = data?.procurement?.typesConnection?.nodes?.[0]

                    return {
                      title:
                        requestType?.name ||
                        i18n.t('procurement.purchase_requests.settings.types.detail.breadcrumb'),
                      illustration: 'receipt',
                    }
                  })
                },
              },
            },
          ],
        },
      ],
    },
  ]
}

export default settingsRoutes
